/**
 * @quotech/ui/bottomDock — Context provider
 *
 * Lifted from `src/contexts/BottomDockContext.jsx` (master SPA) on
 * 2026-05-02 as Phase 1A of the BottomDock universalisation. Behaviour
 * is identical to the legacy provider so existing consumers can swap
 * the import without API changes.
 *
 * SAFE-FALLBACK: when used OUTSIDE a provider, `useBottomDock`
 * returns a no-op object instead of throwing — this matches the
 * legacy behaviour and lets pages register actions optimistically
 * without forcing every test harness to wrap in the provider.
 */

import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
  type ReactNode,
} from 'react'

import type {
  BottomDockAction,
  BottomDockContextValue,
  BottomDockKpiData,
} from './types'

const BottomDockContext = createContext<BottomDockContextValue | null>(null)

const NOOP_VALUE: BottomDockContextValue = {
  customAction: null,
  customActions: [],
  registerAction: () => {},
  unregisterAction: () => {},
  kpiData: null,
  setKpiData: () => {},
  clearKpiData: () => {},
}

export interface BottomDockProviderProps {
  children: ReactNode
}

export function BottomDockProvider({ children }: BottomDockProviderProps) {
  const [customActions, setCustomActions] = useState<BottomDockAction[]>([])
  const [kpiData, setKpiDataRaw] = useState<BottomDockKpiData | null>(null)

  const registerAction = useCallback((action: BottomDockAction) => {
    setCustomActions((prev) => {
      // Replace by id — registering the same id twice updates rather
      // than duplicates. This matches the legacy contract used by
      // Quote Sheet (Upload Quote / Variation buttons).
      const filtered = prev.filter((a) => a.id !== action.id)
      return [...filtered, action]
    })
  }, [])

  const unregisterAction = useCallback((id: string) => {
    setCustomActions((prev) => prev.filter((a) => a.id !== id))
  }, [])

  const setKpiData = useCallback((data: BottomDockKpiData | null) => {
    setKpiDataRaw(data)
  }, [])

  const clearKpiData = useCallback(() => {
    setKpiDataRaw(null)
  }, [])

  const value = useMemo<BottomDockContextValue>(
    () => ({
      customAction: customActions[0] ?? null,
      customActions,
      registerAction,
      unregisterAction,
      kpiData,
      setKpiData,
      clearKpiData,
    }),
    [customActions, kpiData, registerAction, unregisterAction, setKpiData, clearKpiData],
  )

  return <BottomDockContext.Provider value={value}>{children}</BottomDockContext.Provider>
}

export function useBottomDock(): BottomDockContextValue {
  const ctx = useContext(BottomDockContext)
  return ctx ?? NOOP_VALUE
}
